import { useAsync } from '@src/hooks/core/core-hook-imports.js';
import { useDependenciesContext } from '@src/contexts/dependencies.jsx';

/**
 * @typedef {object} UseProviderSpecialtiesSearchReturn
 * @property {function} run the function that can be run to rerun the provider specialties search
 * with the current parameters
 * @property {string} loading true if the provider specialties search is currently running and false if
 * it either has not been started or has alread finished
 * @property {object} error the error, if any, for the provider specialties search
 * @property {object} response the provider specialties search results (in the form of a standard Search
 * API REST response)
 */

/**
 * Uses the Search API's Provider Search View to fetch provider specialties.
 *
 * @param {string} search the user's searched text
 * @param {boolean} immediate whether or not to run the function immediately (if false,
 * you must manually call the returned run function to run it)
 * @returns {UseProviderSpecialtiesSearchReturn}
 *
 * @example
 * const { run, loading, error, response } = useProviderSpecialtiesSearch({ search: 'Primary Care' });
*/
const useProviderSpecialtiesSearch = ({ search, immediate = false }) => {
  const { searchApiService } = useDependenciesContext();

  const getData = async () => {
    const allSearchApiResults = await searchApiService.searchProviderSpecialties({ searchedText: search });
    const fullList = allSearchApiResults.map(r => r.rows).flat();

    let specialtyList = [];
    if (fullList) {
      fullList.forEach(specialty => {
        const splitSpecialties = specialty.field_specialty_list.split(', ');
        if (splitSpecialties.length > 0 && splitSpecialties[0] !== '') {
          specialtyList = [...specialtyList, ...splitSpecialties];
        }
      });
    }

    const uniqueList = [...new Set(specialtyList)];
    uniqueList.sort((a, b) => a.localeCompare(b));

    return uniqueList;
  };

  return useAsync(getData, immediate);
};

export default useProviderSpecialtiesSearch;
